import { BrowserMicroSentryClient } from '@micro-sentry/browser'
import type { SentryRequestBody } from '@micro-sentry/core'
import type { Config } from '@/core/types'
import ConfigReader from '@/core/configReader'
import { LogLevel } from '@/core/logger/types'
import { getUnhandledRejectionEventHandler } from '@/utils/unhandledRejection'

class SentryMonitoringService {
  constructor() {
    this.config = ConfigReader.config()
  }

  private readonly config: Config

  private microSentryClient?: BrowserMicroSentryClient

  private readonly ignoreErrors = [
    'ResizeObserver loop limit exceeded', // We can safely ignore this error: see https://github.com/WICG/resize-observer/issues/38 for details
    'ResizeObserver loop completed with undelivered notifications.', // A variant of the above error, see https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver
    'Non-Error promise rejection captured', // https://github.com/getsentry/sentry-javascript/issues/3440
    'Object captured as exception with keys: code, message',
    /Object captured as promise rejection with keys/,
    'Network Error', // Ignore network errors - these are usually user issues, not system issues
  ]

  public init(appName?: string): void {
    this.microSentryClient = new BrowserMicroSentryClient({
      dsn: this.config.sentry.dsn,
      environment: APP_BUILD_ENV,
      release: [APP_NAME, APP_VERSION].join('@'),
      ignoreErrors: this.ignoreErrors,
      beforeSend: (request: SentryRequestBody) => {
        if (
          request.exception?.values?.some((v) => {
            return v.stacktrace?.frames?.some((f) => !f.filename?.includes('textmagic.com'))
          })
        ) {
          return null as unknown as SentryRequestBody // don't send to Sentry
        }
        if (appName) {
          request.tags ??= {}
          request.tags.appName = appName
        }
        return request
      },
    })

    window.addEventListener('error', this.errorEventHandler.bind(this))
    window.addEventListener('unhandledrejection', getUnhandledRejectionEventHandler(this.logError.bind(this)))
  }

  public setExtras(extras: Partial<Record<string, unknown>>) {
    this.microSentryClient?.setExtras(extras)
  }

  public logError(error: Error, logLevel: LogLevel = LogLevel.ERROR) {
    if (logLevel === LogLevel.NULL) return
    this.microSentryClient?.report(error)
  }

  private sanitize(data: any, seen = new WeakSet()): any {
    if (data == null || typeof data === 'boolean' || typeof data === 'number' || typeof data === 'string') {
      return data
    }

    if (seen.has(data)) return '[Circular]'
    seen.add(data)

    if (Array.isArray(data)) {
      return data.map((item) => this.sanitize(item, seen))
    }

    if (data instanceof FormData) return '[FormData]'
    if (data instanceof URLSearchParams) return '[URLSearchParams]'
    if (data instanceof Blob) return '[Blob]'

    if (typeof data !== 'object') {
      return '[FILTERED]'
    }

    const sensitiveKeysPattern =
      /(token$|secret$|.*_token$|.*password.*|.*pass.*|key$|.*_key$|auth.*|credential.*|.*pin.*|.*phone.*|.*email.*)/i

    const result: Record<string, any> = {}
    for (const key of Object.keys(data)) {
      if (sensitiveKeysPattern.test(key)) {
        result[key] = '[FILTERED]'
      } else {
        result[key] = this.sanitize(data[key], seen)
      }
    }

    return result
  }

  private safeHeaders(headers: Record<string, any> = {}) {
    const SENSITIVE = new Set([
      'authorization',
      'cookie',
      'set-cookie',
      'x-api-key',
      'x-auth-token',
      'proxy-authorization',
    ])
    return Object.fromEntries(Object.entries(headers).filter(([k]) => !SENSITIVE.has(String(k).toLowerCase())))
  }

  private errorEventHandler(evt: ErrorEvent) {
    let err: any = evt
    if (!evt?.message) {
      err = { ...evt, message: "The error was caused due to an 'error' event", stack: evt.error }
    }
    this.logError(err)
    return false
  }
}

export default new SentryMonitoringService()
